import { CalendarDotsIcon, CheckIcon, ClockIcon, TrashIcon } from '@phosphor-icons/react';
import { PageHeader } from 'components/PageHeader';
import { OrderDocumentType, OrderLineStopType } from 'src/types/enums';
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { invariant, nullthrows } from '@utils/invariant';
import classNames from '@utils/classnames';
import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';

import { Breadcrumb } from '../../../../components/Breadcrumb';
import { DataField } from '../../../../components/DataField';
import { formatDate, formatInputDate, formatInputTime } from '../../../../utils/date';
import { extractIntFromText, decimateAndFormatNumber } from '../../../../utils/number';
import { FileUploaderButton } from '../../../document/components/FileUploaderButton';
import { MultiFileUploaderButton } from '../../../document/components/MultiFileUploaderButton';
import { getInvoiceTotals } from '../../../../domain/invoice-totals';
import { getDisplayError } from '../../../../utils/get-display-error';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { InputField } from '../../../../components/input/InputField';
import { Button } from '../../../../components/button/Button';
import { PageHeading } from 'components/PageHeading';
import { SummaryBar } from 'components/SummaryBar';
import { TotalsCard } from 'components/TotalsCard';
import { DelayedSpinner } from '../../../../components/Spinner';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';
import {
  deletePurchase,
  linkPurchaseDocument,
  unlinkPurchaseDocument,
  updatePurchase,
  usePurchase,
} from '../../models';

const updatePurchaseSchema = Yup.object().shape({
  date: Yup.string().min(1, 'Vereist').required('Vereist'),
  invoiceNumber: Yup.string().min(1, 'Vereist').required('Vereist'),
});

const PurchasePage = () => {
  const navigate = useNavigate();
  const { purchaseId } = useParams<{ purchaseId: string }>();
  invariant(purchaseId);
  const { data, isLoading, mutate: refreshPurchase } = usePurchase(purchaseId);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const purchase = nullthrows(data, 'Aankoop niet gevonden');

  const initialValues = {
    date: formatInputDate(purchase.date),
    invoiceNumber: purchase.invoiceNumber,
  };

  const title = `Aankoop ${purchase.invoiceNumber}`;
  return (
    <>
      <PageHeader title={title} />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Aankopen',
              to: '/internal/finance/purchases',
            }}
            currentItem={title}
          />
        }
        rightSide={
          <div className="flex gap-2">
            <FileUploaderButton buttonText="AKF" title="Aankoopfactuur" file={purchase.document} disableUpload />
            <MultiFileUploaderButton
              buttonText="Bijlagen"
              title="Bijlagen"
              files={purchase.documents.map((f) => f.document)}
              onSubmit={async (documents) => {
                let count = 0;
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkPurchaseDocument(purchase.id, doc.id);
                      count += 1;
                      toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                    }
                  }),
                );
                await refreshPurchase();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkPurchaseDocument(purchase.id, docId);
                  await refreshPurchase();
                  toast.success('Bestand gewist');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                }
              }}
            />
            <ConfirmDialog
              triggerColor="danger"
              triggerText={<TrashIcon className="button-icon" />}
              title="Aankoopfactuur verwijderen?"
              submitText="Verwijder aankoopfactuur"
              description={<div>Ben je zeker dat je dit aankoopfactuur wilt verwijderen?</div>}
              onSubmit={async () => {
                try {
                  await deletePurchase(purchase.id);
                  trackEvent('purchase_deleted', { purchase_id: purchase.id });
                  navigate('..');
                  toast.success('Aankoopfactuur verwijderd');
                } catch (err) {
                  captureException(err);
                  console.error(err);
                  toast.error('Kon aankoopfactuur niet verwijderen: ' + getDisplayError(err));
                }
              }}
            />
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-7">
        <SummaryBar className="items-end">
          <div className="pb-2">
            <DataField title="Klant">
              <span className="whitespace-nowrap">
                {purchase.supplier.name} ({purchase.supplier.id})
              </span>
            </DataField>
          </div>

          <div className="w-full max-w-lg">
            <Formik
              initialValues={initialValues}
              validationSchema={updatePurchaseSchema}
              onSubmit={async (newValues) => {
                try {
                  const patch: Partial<typeof newValues> = diff(initialValues, newValues);
                  await updatePurchase(purchase.id, patch);
                  await refreshPurchase();
                  trackEvent('purchase_updated', {
                    purchase_id: purchase.id,
                    fields_changed: Object.keys(patch),
                  });
                  toast.success('Aankoopfactuur aangepast');
                } catch (err: any) {
                  captureException(err);
                  toast.error('Kon aankoopfactuur niet aanpassen: ' + getDisplayError(err));
                }
              }}
            >
              {({ handleSubmit, isSubmitting }) => {
                return (
                  <form className="flex gap-4 items-center" onSubmit={handleSubmit}>
                    <InputField labelText="Factuur datum" type="date" name="date" />
                    <InputField labelText="Factuur nummer" type="text" name="invoiceNumber" />
                    <div>
                      <Button type="submit" color="primary" isDisabled={isSubmitting} isLoading={isSubmitting}>
                        <CheckIcon className="button-icon" />
                      </Button>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </div>
        </SummaryBar>

        <>
          {purchase.orders
            .sort((a, b) => extractIntFromText(a.orderNumber || '') - extractIntFromText(b.orderNumber || ''))
            .map((order) => {
              const orderTotals = getInvoiceTotals(
                order.lines
                  .map((l) =>
                    l.purchases.map((p) => {
                      return {
                        ...p,
                        vatRateId: p.vatRate.id,
                      };
                    }),
                  )
                  .flat(),
              );

              const hasCmr = order.documents.some((v) => v.type === OrderDocumentType.Cmr);

              return (
                <div className="card" key={`order-${order.orderNumber}`}>
                  <div className="card-heading">
                    <Link to={`/internal/orders/${order.id}/general`} className="link-text flex gap-3">
                      <div>
                        Order {order.orderNumber} - {order.customerRef}
                      </div>
                    </Link>

                    <div
                      className={classNames('text-xs font-semibold', {
                        'text-negative-text': !hasCmr,
                        'text-positive-text': hasCmr,
                      })}
                    >
                      {hasCmr ? 'CMR' : 'Geen CMR'}
                    </div>
                  </div>

                  <div className="card-content flex flex-col gap-3.5">
                    {order.lines.map((line) => {
                      return (
                        <div className="flex flex-col gap-3.5" key={`line-${line.id}`}>
                          <div className="flex flex-col gap-2">
                            {line.stops
                              .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                              .map((stop) => {
                                return (
                                  <div
                                    className="grid grid-cols-4 items-center gap-3 text-sm"
                                    key={`line-${line.id}-${stop.id}`}
                                  >
                                    <div className="font-semibold text-fg">
                                      {stop.type === OrderLineStopType.Load ? 'Laden' : 'Lossen'}
                                    </div>
                                    <div className="text-fg-secondary">
                                      {stop.location.country}-{stop.location.name}
                                    </div>

                                    <div className="flex items-center gap-1.5 text-fg-secondary">
                                      <CalendarDotsIcon className="h-3.5 w-3.5 text-fg-muted" />
                                      {formatDate(stop.date)}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-fg-secondary">
                                      <ClockIcon className="h-3.5 w-3.5 text-fg-muted" />
                                      {`${formatInputTime(stop.timeStart)}-${formatInputTime(stop.timeEnd)}`}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>

                          <div className="overflow-hidden rounded-md border border-border">
                            <div className="grid grid-cols-5 gap-x-4 border-b border-border bg-surface-sunken px-3.5 py-2">
                              <div className="label-text">Beschrijving</div>
                              <div className="label-text">Aantal</div>
                              <div className="label-text">Eenheidsprijs</div>
                              <div className="label-text">Totaal</div>
                              <div className="label-text">Btw</div>
                            </div>

                            <div className="grid grid-cols-5 gap-x-4 gap-y-1 px-3.5 py-2.5 text-sm">
                              {line.purchases.map((purchaseLine) => {
                                return (
                                  <React.Fragment key={`sale-${purchaseLine.id}`}>
                                    <div>{purchaseLine.productType.nameNl}</div>
                                    <div>
                                      {decimateAndFormatNumber(purchaseLine.amount, 2, {
                                        decimalSeperator: ',',
                                      })}
                                    </div>
                                    <div className="whitespace-nowrap">
                                      {`€ ${decimateAndFormatNumber(purchaseLine.unitPrice, 2, {
                                        decimalSeperator: ',',
                                      })}`}
                                    </div>
                                    <div className="whitespace-nowrap">
                                      {`€ ${decimateAndFormatNumber(
                                        (purchaseLine.amount * purchaseLine.unitPrice) / 100,
                                        2,
                                        {
                                          decimalSeperator: ',',
                                        },
                                      )}`}
                                    </div>
                                    <div
                                      className={classNames('whitespace-nowrap', {
                                        'text-negative-text':
                                          purchaseLine.vatRate.id !== purchase.supplier.defaultVatRate.id,
                                      })}
                                    >
                                      {`€ ${decimateAndFormatNumber(
                                        (purchaseLine.amount *
                                          purchaseLine.unitPrice *
                                          purchaseLine.vatRatePercentage) /
                                          1000000,
                                        2,
                                        {
                                          decimalSeperator: ',',
                                        },
                                      )} - ${decimateAndFormatNumber(purchaseLine.vatRatePercentage, 2, {
                                        decimalSeperator: ',',
                                      })}%`}
                                    </div>

                                    {!!purchaseLine.externalNote && (
                                      <div className="col-span-5">
                                        <div className="text-sm">Extra informatie: {purchaseLine.externalNote}</div>
                                      </div>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="whitespace-nowrap text-xs text-fg-muted">
                      {`Order totaal: € ${decimateAndFormatNumber(orderTotals.totalExclVat, 2, {
                        decimalSeperator: ',',
                      })}`}
                    </div>
                  </div>
                </div>
              );
            })}
        </>

        <TotalsCard
          rows={[
            {
              label: 'Totaal excl. btw:',
              value: `€ ${decimateAndFormatNumber(purchase.totalExclVat, 2, { decimalSeperator: ',' })}`,
            },
            {
              label: 'Totaal btw:',
              value: `€ ${decimateAndFormatNumber(purchase.totalInclVat - purchase.totalExclVat, 2, {
                decimalSeperator: ',',
              })}`,
            },
            {
              label: 'Totaal incl. btw:',
              isEmphasised: true,
              value: `€ ${decimateAndFormatNumber(purchase.totalInclVat, 2, { decimalSeperator: ',' })}`,
            },
          ]}
        />
      </div>
    </>
  );
};

export default PurchasePage;
